import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Alert,
  Autocomplete,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  LocalShipping as SupplierIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditIcon,
  Payment as PaymentIcon,
  Receipt as TaxIcon,
  Code as CodeIcon,
  Language as LanguageIcon,
  ContactPhone as ContactPhoneIcon
} from '@mui/icons-material';
import { Supplier, CreateSupplierDto, UpdateSupplierDto } from '../../types/suppliers.types';
import suppliersService from '../../services/suppliers.service';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import { useLanguage } from '../../contexts/LanguageContext';

interface SupplierDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  supplier: Supplier | null;
}

interface Account {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  accountType: string;
}

const SupplierDialog: React.FC<SupplierDialogProps> = ({ open, onClose, onSuccess, supplier }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const t = {
    addSupplier: isRTL ? 'إضافة مورد جديد' : 'Add New Supplier',
    editSupplier: isRTL ? 'تعديل مورد' : 'Edit Supplier',
    basicInfo: isRTL ? 'المعلومات الأساسية' : 'Basic Information',
    contactInfo: isRTL ? 'معلومات الاتصال' : 'Contact Information',
    financialInfo: isRTL ? 'المعلومات المالية' : 'Financial Information',
    accountingInfo: isRTL ? 'المعلومات المحاسبية' : 'Accounting Information',
    supplierCode: isRTL ? 'كود المورد' : 'Supplier Code',
    supplierName: isRTL ? 'اسم المورد (EN)' : 'Supplier Name (EN)',
    supplierNameAr: isRTL ? 'اسم المورد (AR)' : 'Supplier Name (AR)',
    contactPerson: isRTL ? 'الشخص المسؤول' : 'Contact Person',
    phone: isRTL ? 'الهاتف' : 'Phone',
    mobile: isRTL ? 'الجوال' : 'Mobile',
    email: isRTL ? 'البريد الإلكتروني' : 'Email',
    address: isRTL ? 'العنوان' : 'Address',
    taxNumber: isRTL ? 'الرقم الضريبي' : 'Tax Number',
    creditLimit: isRTL ? 'الحد الائتماني' : 'Credit Limit',
    paymentTermDays: isRTL ? 'مدة الدفع (أيام)' : 'Payment Terms (Days)',
    supplierAccount: isRTL ? 'حساب المورد' : 'Supplier Account',
    selectSupplierAccount: isRTL ? 'اختر حساب المورد' : 'Select Supplier Account',
    isActive: isRTL ? 'نشط' : 'Active',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    optional: isRTL ? 'اختياري' : 'Optional',
    required: isRTL ? 'مطلوب' : 'Required',
    loadingAccounts: isRTL ? 'جاري تحميل الحسابات...' : 'Loading accounts...',
    noAccountsFound: isRTL ? 'لا توجد حسابات نهائية' : 'No final accounts found'
  };

  const [formData, setFormData] = useState({
    supplierCode: '',
    supplierName: '',
    supplierNameAr: '',
    contactPerson: '',
    phone: '',
    mobile: '',
    email: '',
    address: '',
    taxNumber: '',
    creditLimit: 0,
    paymentTermDays: 30,
    accountId: null as number | null,
    isActive: true,
  });

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadAccounts();
      if (supplier) {
        setFormData({
          supplierCode: supplier.supplierCode || '',
          supplierName: supplier.supplierName || '',
          supplierNameAr: supplier.supplierNameAr || '',
          contactPerson: supplier.contactPerson || '',
          phone: supplier.phone || '',
          mobile: supplier.mobile || '',
          email: supplier.email || '',
          address: supplier.address || '',
          taxNumber: supplier.taxNumber || '',
          creditLimit: supplier.creditLimit || 0,
          paymentTermDays: supplier.paymentTermDays || 30,
          accountId: supplier.accountId || null,
          isActive: supplier.isActive !== undefined ? supplier.isActive : true,
        });
      } else {
        setFormData({
          supplierCode: suppliersService.generateCode(),
          supplierName: '',
          supplierNameAr: '',
          contactPerson: '',
          phone: '',
          mobile: '',
          email: '',
          address: '',
          taxNumber: '',
          creditLimit: 0,
          paymentTermDays: 30,
          accountId: null,
          isActive: true,
        });
      }
    }
  }, [supplier, open]);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const data = await chartOfAccountsService.getAll();
      // ✅ فلترة الحسابات النهائية النشطة فقط
      const finalAccounts = data.filter((acc: any) => 
        acc.isFinalAccount && acc.isActive
      );
      setAccounts(finalAccounts);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleGenerateCode = () => {
    setFormData((prev) => ({ ...prev, supplierCode: suppliersService.generateCode() }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (supplier) {
        const updateData: UpdateSupplierDto = {
          supplierId: supplier.supplierId,
          supplierName: formData.supplierName,
          supplierNameAr: formData.supplierNameAr,
          contactPerson: formData.contactPerson || undefined,
          phone: formData.phone || undefined,
          mobile: formData.mobile || undefined,
          email: formData.email || undefined,
          address: formData.address || undefined,
          taxNumber: formData.taxNumber || undefined,
          creditLimit: formData.creditLimit || undefined,
          paymentTermDays: formData.paymentTermDays || undefined,
          accountId: formData.accountId ?? undefined,
          isActive: formData.isActive,
        };
        await suppliersService.update(supplier.supplierId, updateData);
      } else {
        const createData: CreateSupplierDto = {
          supplierCode: formData.supplierCode,
          supplierName: formData.supplierName,
          supplierNameAr: formData.supplierNameAr,
          contactPerson: formData.contactPerson || undefined,
          phone: formData.phone || undefined,
          mobile: formData.mobile || undefined,
          email: formData.email || undefined,
          address: formData.address || undefined,
          taxNumber: formData.taxNumber || undefined,
          creditLimit: formData.creditLimit || undefined,
          paymentTermDays: formData.paymentTermDays || undefined,
          accountId: formData.accountId ?? undefined,
        };
        await suppliersService.create(createData);
      }
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error saving supplier:', error);
      alert('Failed to save supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const selectedAccount = accounts.find(acc => acc.accountId === formData.accountId);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth 
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupplierIcon />
          <Typography variant="h6" fontWeight="bold">
            {supplier ? t.editSupplier : t.addSupplier}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Basic Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BusinessIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.basicInfo}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label={t.supplierCode}
                name="supplierCode"
                value={formData.supplierCode}
                onChange={handleChange}
                disabled={!!supplier}
                InputProps={{
                  startAdornment: <CodeIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
              {!supplier && (
                <IconButton color="primary" onClick={handleGenerateCode} sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                  <RefreshIcon />
                </IconButton>
              )}
            </Box>

            <TextField
              fullWidth
              label={`${t.taxNumber} (${t.optional})`}
              name="taxNumber"
              value={formData.taxNumber}
              onChange={handleChange}
              InputProps={{
                startAdornment: <TaxIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <TextField
              fullWidth
              label={`${t.supplierName} *`}
              name="supplierName"
              value={formData.supplierName}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <TextField
              fullWidth
              label={`${t.supplierNameAr} *`}
              name="supplierNameAr"
              value={formData.supplierNameAr}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.contactInfo}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField
              fullWidth
              label={`${t.contactPerson} (${t.optional})`}
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              InputProps={{
                startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <TextField
              fullWidth
              label={`${t.phone} (${t.optional})`}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              InputProps={{
                startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <TextField
              fullWidth
              label={`${t.mobile} (${t.optional})`}
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              InputProps={{
                startAdornment: <ContactPhoneIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <TextField
              fullWidth
              label={`${t.email} (${t.optional})`}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <Box sx={{ gridColumn: 'span 2' }}>
              <TextField
                fullWidth
                label={`${t.address} (${t.optional})`}
                name="address"
                value={formData.address}
                onChange={handleChange}
                multiline
                rows={2}
                InputProps={{
                  startAdornment: <HomeIcon sx={{ mr: 1, mt: 1, color: 'action.active' }} />
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Financial Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CreditIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.financialInfo}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField
              fullWidth
              label={`${t.creditLimit} (${t.optional})`}
              name="creditLimit"
              type="number"
              value={formData.creditLimit}
              onChange={handleChange}
              inputProps={{ step: 0.001 }}
              InputProps={{
                startAdornment: <CreditIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />

            <TextField
              fullWidth
              label={`${t.paymentTermDays} (${t.optional})`}
              name="paymentTermDays"
              type="number"
              value={formData.paymentTermDays}
              onChange={handleChange}
              InputProps={{
                startAdornment: <PaymentIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Box>
        </Box>

        {/* Accounting Information */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccountBalanceIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.accountingInfo}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Autocomplete
            fullWidth
            options={accounts}
            getOptionLabel={(option) => 
              `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName} (${option.accountType})`
            }
            value={selectedAccount || null}
            onChange={(_, newValue) => {
              setFormData({ ...formData, accountId: newValue?.accountId || null });
            }}
            loading={loadingAccounts}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${t.supplierAccount} (${t.optional})`}
                placeholder={t.selectSupplierAccount}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <AccountBalanceIcon sx={{ ml: 1, mr: 1, color: 'action.active' }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <li key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option.accountCode}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isRTL ? option.accountNameAr : option.accountName} • {option.accountType}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            noOptionsText={t.noAccountsFound}
          />

          {!formData.accountId && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {isRTL 
                ? 'ربط المورد بحساب محاسبي نهائي يساعد في تتبع المديونية والمدفوعات بدقة'
                : 'Linking supplier to a final account helps track payables and payments accurately'
              }
            </Alert>
          )}
        </Box>

        {/* Active Status */}
        {supplier && (
          <Box sx={{ mt: 3, p: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  color="success"
                />
              }
              label={
                <Typography variant="body1" fontWeight="bold">
                  {t.isActive}
                </Typography>
              }
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={handleClose} disabled={loading} size="large">
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          size="large"
          sx={{
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
            '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
          }}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierDialog;